import React, { useState, useEffect, useRef } from 'react';
import { authAPI, controlAPI } from '../api/api';
import { useSocket } from '../context/SocketContext';
import toast from 'react-hot-toast';

export default function ControlPage() {
  const { startWebRTCCall, endWebRTCCall, accessResponses, isConnected, latestVideoFrame, latestScreenFrame } = useSocket();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCall, setActiveCall] = useState(null); // { type, stream }
  const videoRef = useRef(null);
  const [alarmMsg, setAlarmMsg] = useState('Emergency! Contact your parents immediately!');
  const [showAlarmModal, setShowAlarmModal] = useState(false);

  useEffect(() => {
    authAPI.children().then(res => {
      setChildren(res.data.children);
      if (res.data.children[0]) setSelectedChild(res.data.children[0]);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (selectedChild) loadDeviceStatus(selectedChild._id);
  }, [selectedChild]);

  const [cameraImageUrl, setCameraImageUrl] = useState(null);
  const audioLevelsRef = useRef([]); // Store recent audio levels for visualization
  const lastHandledResponseRef = useRef('');
  const [cameraFacing, setCameraFacing] = useState('front');
  const requestedFacingRef = useRef('front');

  // Handle access responses from child
  useEffect(() => {
    if (accessResponses.length === 0) return;
    const latest = accessResponses[0];
    const responseKey = `${latest.childId}_${latest.type}_${latest.approved}`;
    if (responseKey === lastHandledResponseRef.current) return;
    lastHandledResponseRef.current = responseKey;

    if (latest.approved) {
      toast.success(`${latest.childName} approved ${latest.type} access!`);

      // Ensure previous stream is fully closed before starting a new one.
      endWebRTCCall();
      setCameraImageUrl(null);
      audioLevelsRef.current = [];
      setActiveCall({ type: latest.type, stream: null });
      
      // Initiate camera/audio streaming
      startWebRTCCall(
        latest.childId,
        latest.type,
        (frameOrStream) => {
        console.log('📹 Stream callback triggered, type:', latest.type);
        
        if (latest.type === 'camera') {
          if (typeof frameOrStream !== 'string') return;
          setCameraImageUrl(frameOrStream);
        } else if (latest.type === 'microphone') {
          if (frameOrStream && frameOrStream.length) {
            // Calculate audio level from samples
            let sum = 0;
            for (let i = 0; i < frameOrStream.length; i++) {
              sum += frameOrStream[i] * frameOrStream[i];
            }
            const rms = Math.sqrt(sum / frameOrStream.length);
            const level = Math.min(100, Math.floor(rms * 500)); // Normalize to 0-100
            audioLevelsRef.current.push(level);
            if (audioLevelsRef.current.length > 30) {
              audioLevelsRef.current.shift();
            }

            // Keep UI reactive for the audio meter without resetting stream state.
            setActiveCall(prev => (prev ? { ...prev } : prev));
          }
        }
      },
        latest.type === 'camera' ? { facing: requestedFacingRef.current } : {}
      );
    } else {
      toast.error(`${latest.childName} denied ${latest.type} access`);
    }
  }, [accessResponses, startWebRTCCall, endWebRTCCall]);

  const loadDeviceStatus = async (childId) => {
    try {
      const res = await controlAPI.getDeviceStatus(childId);
      setDeviceStatus(res.data);
    } catch {}
  };

  const handleControl = async (action) => {
    if (!selectedChild) return toast.error('Select a child device first');
    if (!isConnected) return toast.error('Not connected to server');

    const childId = selectedChild._id;
    try {
      switch (action) {
        case 'lock':
          await controlAPI.lockDevice(childId);
          toast.success('🔒 Lock command sent to device');
          break;
        case 'camera':
          requestedFacingRef.current = cameraFacing;
          await controlAPI.requestCamera(childId, cameraFacing);
          toast(`📷 ${cameraFacing} camera request sent — waiting for child approval...`, { icon: '⏳' });
          break;
        case 'mic':
          await controlAPI.requestMic(childId);
          toast('🎤 Microphone request sent — waiting for child approval...', { icon: '⏳' });
          break;
        case 'screen':
          await controlAPI.requestScreen(childId);
          toast('📱 Screen share request sent — waiting for child approval...', { icon: '⏳' });
          break;
        case 'alarm':
          setShowAlarmModal(true);
          break;
        default:
          toast.error('Unknown control action');
          break;
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Command failed');
    }
  };

  const triggerAlarm = async () => {
    try {
      await controlAPI.triggerAlarm(selectedChild._id, alarmMsg);
      toast.success('🔔 Emergency alarm triggered!');
      setShowAlarmModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to trigger alarm');
    }
  };

  const endCall = () => {
    endWebRTCCall();
    setActiveCall(null);
    setCameraImageUrl(null);
    audioLevelsRef.current = [];
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const hasPermission = (perm) => deviceStatus?.permissions?.[perm] === true;

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>🎮 Device Control</h2>

      {/* Child Selector */}
      <div className="child-selector">
        {children.map(child => (
          <div key={child._id}
            className={`child-chip ${selectedChild?._id === child._id ? 'active' : ''}`}
            onClick={() => setSelectedChild(child)}
          >
            <div className="child-avatar">{child.name[0]}</div>
            {child.name}
          </div>
        ))}
      </div>

      {selectedChild && (
        <>
          {/* Device Status */}
          {deviceStatus && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header">
                <div className="card-title">📱 {selectedChild.name}'s Device Status</div>
                <span className={`badge ${deviceStatus.isOnline ? 'badge-online' : 'badge-offline'}`}>
                  {deviceStatus.isOnline ? '● Online' : '○ Offline'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {Object.entries(deviceStatus.permissions || {}).map(([key, val]) => (
                  <div key={key} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: val ? 'rgba(16,185,129,0.1)' : 'rgba(107,114,128,0.1)',
                    border: `1px solid ${val ? 'rgba(16,185,129,0.3)' : 'var(--border)'}`,
                    borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600,
                    color: val ? 'var(--green)' : 'var(--text-muted)'
                  }}>
                    {val ? '✓' : '✗'} {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                  </div>
                ))}
              </div>
              {deviceStatus.permissionsGrantedAt && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
                  Permissions granted at: {new Date(deviceStatus.permissionsGrantedAt).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {/* Control Buttons */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <div className="card-title">🎛️ Remote Controls</div>
              {!isConnected && <span className="badge badge-danger">Server Offline</span>}
            </div>
            <div className="control-grid">
              <button className="control-btn lock" onClick={() => handleControl('lock')}
                disabled={!isConnected || !hasPermission('remoteLock')}>
                <div className="control-btn-icon">🔒</div>
                Lock Device
                {!hasPermission('remoteLock') && <span style={{ fontSize: 10, color: 'var(--red)' }}>No permission</span>}
              </button>

              <button className="control-btn camera" onClick={() => handleControl('camera')}
                disabled={!isConnected || !hasPermission('camera') || !!activeCall}>
                <div className="control-btn-icon">📷</div>
                Camera View
                {!hasPermission('camera') && <span style={{ fontSize: 10, color: 'var(--red)' }}>No permission</span>}
              </button>
              <button
                className="control-btn"
                onClick={() => setCameraFacing(prev => (prev === 'front' ? 'back' : 'front'))}
                disabled={!!activeCall}
                style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.35)' }}
              >
                <div className="control-btn-icon">🔄</div>
                Camera: {cameraFacing}
              </button>

              <button className="control-btn mic" onClick={() => handleControl('mic')}
                disabled={!isConnected || !hasPermission('microphone') || !!activeCall}>
                <div className="control-btn-icon">🎤</div>
                Listen Mic
                {!hasPermission('microphone') && <span style={{ fontSize: 10, color: 'var(--red)' }}>No permission</span>}
              </button>

              <button className="control-btn screen" onClick={() => handleControl('screen')}
                disabled={!isConnected || !hasPermission('screenShare') || !!activeCall}>
                <div className="control-btn-icon">🖥️</div>
                Screen Share
                {!hasPermission('screenShare') && <span style={{ fontSize: 10, color: 'var(--red)' }}>No permission</span>}
              </button>

              <button className="control-btn alarm" onClick={() => handleControl('alarm')}
                disabled={!isConnected}>
                <div className="control-btn-icon">🔔</div>
                Alarm
              </button>
            </div>
          </div>

          {/* Active WebRTC Stream */}
          {activeCall && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header">
                <div className="card-title" style={{ color: 'var(--green)' }}>
                  🔴 LIVE — {activeCall.type.toUpperCase()} STREAM
                </div>
                <button className="btn btn-danger btn-sm" onClick={endCall}>End Call</button>
              </div>
              {activeCall.type === 'camera' ? (
                <div className="remote-stream-shell camera-stream-shell">
                  {(latestVideoFrame || cameraImageUrl) ? (
                    <img 
                      ref={videoRef}
                      src={latestVideoFrame || cameraImageUrl}
                      className="remote-stream-image"
                      alt="Child camera feed"
                      onError={() => console.error('Image failed to load')}
                    />
                  ) : (
                    <div className="remote-stream-waiting">
                      ⏳ Waiting for camera feed...
                    </div>
                  )}
                </div>
              ) : activeCall.type === 'microphone' ? (
                <div className="remote-stream-shell mic-stream-shell">
                  <div style={{ fontSize: 48 }}>🎤</div>
                  <span style={{ fontSize: 18, color: 'var(--green)', fontWeight: 600 }}>
                    Audio streaming live...
                  </span>
                  
                  {/* Audio level visualizer */}
                  <div className="audio-meter">
                    {audioLevelsRef.current.map((level, i) => (
                      <div
                        key={i}
                        style={{
                          width: 6,
                          height: `${Math.max(5, level / 2)}px`,
                          background: `hsl(${Math.floor(level / 3)}, 100%, 50%)`,
                          borderRadius: 2,
                          transition: 'height 0.1s'
                        }}
                      />
                    ))}
                  </div>
                  
                  <div style={{
                    fontSize: 12, color: 'var(--text-muted)',
                    textAlign: 'center'
                  }}>
                    Microphone active • Quality: Live
                  </div>
                </div>
              ) : (
                <div className="remote-stream-shell screen-stream-shell">
                  {latestScreenFrame ? (
                    <img
                      src={latestScreenFrame}
                      alt="Child screen share"
                      className="remote-stream-image"
                    />
                  ) : (
                    <span style={{ fontSize: 14, color: 'var(--yellow)' }}>⏳ Waiting for screen feed...</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Important Warning */}
          <div style={{
            background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 12, padding: '14px 18px', fontSize: 13, color: 'var(--yellow)'
          }}>
            ⚠️ <strong>Transparency Notice:</strong> All remote access actions are logged and displayed on the child's device.
            Camera and microphone access require real-time approval from the child device.
          </div>
        </>
      )}

      {children.length === 0 && (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">📱</div>
            <h3>No Devices Linked</h3>
            <p>Link a child device from the Settings page first.</p>
          </div>
        </div>
      )}

      {/* Alarm Modal */}
      {showAlarmModal && (
        <div className="modal-overlay" onClick={() => setShowAlarmModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">🔔 Trigger Emergency Alarm</div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setShowAlarmModal(false)}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              This will play a loud alarm on {selectedChild?.name}'s device and display your message.
            </p>
            <div className="form-group">
              <label className="form-label">Message to display on device</label>
              <textarea className="form-input" rows={3} value={alarmMsg}
                onChange={e => setAlarmMsg(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setShowAlarmModal(false)}>Cancel</button>
              <button className="btn btn-danger" style={{ flex: 1 }} onClick={triggerAlarm}>🔔 Trigger Alarm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
